import { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import {
  configProps,
  ContractSelect,
  DataGridDropdowns,
  DriverSelect,
  InputContext,
  PossibleColumnProps,
  RowFromView,
  ViewNames,
} from "../types";
import CellEditor from "./CellEditor";
import { InputNumber } from "primereact/inputnumber";
import { useSetDataByView } from "../hooks/useSetDataByView/useSetDataByView";
import { extractApiError } from "../api/errors.ts";
import { formGroupLabel } from "../helpers/formGroupLabel.ts";
import { useAppDispatch, useAppSelector } from "../redux/store.ts";
import { createFormSlice } from "../redux/slices/createForm.ts";
import { addNewSlice } from "../redux/slices/addNew.ts";
import { RadioButton } from "primereact/radiobutton";
import { useShowToast } from "../contexts/ToastContext.tsx";
import { useSetTransportOrderData } from "../hooks/useSetDataByView/useTransportOrderData.ts";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { TransportOrdersForm } from "../types/transportOrders.ts";
import { transportOrdersConfig } from "../pages/transportOrders/transportOrdersConfig.tsx";

type AddNewEntryProps<
  TView extends ViewNames,
  TDropdowns extends DataGridDropdowns,
> = {
  columns: PossibleColumnProps<RowFromView<TView>>[];
  dropdownConfigs: TDropdowns;
  config: configProps<TView>;
  inputContext?: InputContext;
};

export default function AddNewEntry<
  TView extends ViewNames,
  TDropdowns extends DataGridDropdowns,
>({
  columns: defaultColumns,
  dropdownConfigs: originalDropdownConfigs,
  config: defaultConfig,
  inputContext,
}: AddNewEntryProps<TView, TDropdowns>) {
  const [entryFormContent, setEntryFormContent] = useState<
    Partial<
      RowFromView<TView> & {
        // ToDo: this is not great
        driver?: number;
        trucks?: number | string | null;
        trailers?: number | string | null;
        product?: number;
        salesContract?: number;
        purchaseContract?: number;
        seller?: number;
        buyer?: number;
        carrier?: number;
        purchasePrice?: number | string | null;
        salesPrice?: number | string | null;
      }
    >
  >({});
  const [repeatCount, setRepeatCount] = useState<
    number | "from_sales_contract" | "from_purchase_contract"
  >(1);
  const [isSubmitting, setSubmitting] = useState(false);

  const dispatch = useAppDispatch();
  const { isOpen, initialValues, dialogView } = useAppSelector(
    (state) => state.createForm,
  );
  const { lastAdded } = useAppSelector((state) => state.addNew);

  const showToast = useShowToast();
  const { setData: setTransportOrderData } = useSetTransportOrderData();
  const { setData: setDefaultData } = useSetDataByView<TView>();

  // Use transport orders config when dialogView is "transportOrders"
  const { columns, config } = useMemo(() => {
    if (dialogView === "transportOrders") {
      return {
        columns: transportOrdersConfig.columns,
        config: transportOrdersConfig,
      };
    }
    return {
      columns: defaultColumns,
      config: defaultConfig,
    };
  }, [dialogView, defaultColumns, defaultConfig]);

  useEffect(() => {
    if (initialValues) {
      setEntryFormContent((prev) => ({
        ...prev,
        ...initialValues,
      }));
      if (
        initialValues.repeatCount !== undefined &&
        (typeof initialValues.repeatCount === "number" ||
          initialValues.repeatCount === "from_sales_contract" ||
          initialValues.repeatCount === "from_purchase_contract")
      ) {
        setRepeatCount(initialValues.repeatCount);
      }
    }
  }, [initialValues]);

  const selectedDriver = useMemo(() => {
    if (!entryFormContent.driver) return null;
    return (
      originalDropdownConfigs["drivers"] as DriverSelect[] | undefined
    )?.find(
      (driver: DriverSelect) =>
        entryFormContent.driver && driver.value === entryFormContent.driver,
    );
  }, [originalDropdownConfigs, entryFormContent.driver]);

  const selectedSalesContract = useMemo(() => {
    if (!entryFormContent.salesContract) return null;
    return (
      originalDropdownConfigs["salesContracts"] as ContractSelect[] | undefined
    )?.find((contract) => contract.value === entryFormContent.salesContract);
  }, [originalDropdownConfigs, entryFormContent.salesContract]);

  const selectedPurchaseContract = useMemo(() => {
    if (!entryFormContent.purchaseContract) return null;
    return (
      originalDropdownConfigs["purchaseContracts"] as
        | ContractSelect[]
        | undefined
    )?.find((contract) => contract.value === entryFormContent.purchaseContract);
  }, [originalDropdownConfigs, entryFormContent.purchaseContract]);

  const selectedProduct = useMemo(() => {
    if (!entryFormContent.product) return null;
    return originalDropdownConfigs["products"]?.find(
      (product) => product.value === entryFormContent.product,
    );
  }, [originalDropdownConfigs, entryFormContent.product]);

  const childDropdownConfigs = useMemo(() => {
    return {
      ...originalDropdownConfigs,
      trucks:
        selectedDriver?.data.vehicles
          .filter((vehicle) => vehicle.type === "truck")
          .map((vehicle) => ({
            label: vehicle.number,
            value: vehicle.id,
            data: vehicle,
          })) ?? [],
      trailers:
        selectedDriver?.data.vehicles
          .filter((vehicle) => vehicle.type !== "truck")
          .map((vehicle) => ({
            label: vehicle.number,
            value: vehicle.id,
            data: vehicle,
          })) ?? [],
      salesContracts:
        selectedPurchaseContract || selectedProduct
          ? (originalDropdownConfigs.salesContracts?.filter(
              (contract) =>
                contract.data.product.id ===
                (selectedPurchaseContract?.data.product.id ??
                  selectedProduct?.value),
            ) ?? [])
          : (originalDropdownConfigs.salesContracts ?? []),
      purchaseContracts:
        selectedSalesContract || selectedProduct
          ? (originalDropdownConfigs.purchaseContracts?.filter(
              (contract) =>
                contract.data.product.id ===
                (selectedSalesContract?.data.product.id ??
                  selectedProduct?.value),
            ) ?? [])
          : (originalDropdownConfigs.purchaseContracts ?? []),
    };
  }, [
    originalDropdownConfigs,
    selectedDriver,
    selectedSalesContract,
    selectedPurchaseContract,
    selectedProduct,
  ]);

  // set the value of the dropdown to the newly added value
  useEffect(() => {
    if (lastAdded && lastAdded.field && lastAdded.value) {
      setEntryFormContent((prev) => ({
        ...prev,
        [lastAdded.field]: lastAdded.value,
      }));
      dispatch(addNewSlice.actions.clearLastAdded());
    }
  }, [lastAdded, dispatch]);

  useEffect(() => {
    if (!entryFormContent.driver) {
      setEntryFormContent((prev) => ({
        ...prev,
        trucks: undefined,
        trailers: undefined,
        carrier: undefined,
      }));
      return;
    }

    if (selectedDriver) {
      const driverDefaults: {
        trucks: number | undefined;
        trailers?: number | undefined;
        carrier?: number | undefined;
      } = {
        trucks: selectedDriver.data.vehicles.find(
          (v) => v.default_truck_for_driver,
        )?.id,
        trailers: selectedDriver.data.vehicles.find(
          (v) => v.default_trailer_for_driver,
        )?.id,
        carrier: selectedDriver.data.company?.id,
      };
      setEntryFormContent((prev) => ({
        ...prev,
        ...driverDefaults,
      }));
    } else {
      setEntryFormContent((prev) => ({
        ...prev,
        trucks: undefined,
        trailers: undefined,
        carrier: undefined,
      }));
    }
  }, [
    entryFormContent.driver,
    childDropdownConfigs.trailers,
    childDropdownConfigs.trucks,
    selectedDriver,
  ]);

  // if contract is selected, product is set to undefined so it can be set from the contract's details on BE
  useEffect(() => {
    if (selectedSalesContract || selectedPurchaseContract) {
      setEntryFormContent((prev) => ({
        ...prev,
        product: undefined,
      }));
    }
  }, [selectedSalesContract, selectedPurchaseContract]);

  useEffect(() => {
    if (selectedSalesContract && selectedSalesContract.data.price) {
      setEntryFormContent((prev) => ({
        ...prev,
        unloadingLocation: selectedSalesContract.data.location?.id ?? undefined,
        salesPrice: selectedSalesContract.data.price ?? undefined,
        buyer: selectedSalesContract.data.client?.id ?? undefined,
      }));
    }
  }, [selectedSalesContract]);

  useEffect(() => {
    if (selectedPurchaseContract && selectedPurchaseContract.data.price) {
      setEntryFormContent((prev) => ({
        ...prev,
        loadingLocation:
          selectedPurchaseContract.data.location?.id ?? undefined,
        purchasePrice: selectedPurchaseContract.data.price ?? undefined,
        seller: selectedPurchaseContract.data.client?.id ?? undefined,
      }));
    }
  }, [selectedPurchaseContract]);

  const isEntryFormContentValid = columns
    .filter((column) => {
      if (typeof column.required === "function") {
        return column.required(entryFormContent as Partial<RowFromView<TView>>);
      }
      return column.required;
    })
    .every((column) => {
      const value = entryFormContent[column.field as keyof RowFromView<TView>];
      return value !== null && value !== undefined && value !== "";
    });

  const handleCellContentChange = (field: string, value: unknown) => {
    setEntryFormContent((prev) => ({
      ...prev,
      [field]: value as RowFromView<TView>[keyof RowFromView<TView>],
    }));
  };

  const handleSave = async () => {
    setSubmitting(true);
    let newEntries: Partial<RowFromView<TView>>[] = [];

    try {
      let hasError = false;
      // When creating transport order from contract
      if (dialogView === "transportOrders") {
        const transportEntries: Partial<TransportOrdersForm>[] = [
          {
            ...entryFormContent,
            trucks:
              typeof entryFormContent.trucks === "string"
                ? Number(entryFormContent.trucks)
                : entryFormContent.trucks,
            trailers:
              typeof entryFormContent.trailers === "string"
                ? Number(entryFormContent.trailers)
                : entryFormContent.trailers,
            count: repeatCount,
            id: undefined,
          },
        ];
        const response = await setTransportOrderData(transportEntries);
        response.forEach((response) => {
          if (response && "error" in response) {
            hasError = true;
            showToast({
              severity: "error",
              summary: "Błąd",
              detail: extractApiError(
                response.error as FetchBaseQueryError | SerializedError,
              ),
            });
          }
        });
      } else {
        // Creating new entries based on the view
        if (config.view === "contracts") {
          newEntries = [
            {
              ...entryFormContent,
              contractType: "sales",
              id: undefined,
            },
          ];
        }

        if (config.view === "goodsToTakeAway") {
          newEntries = [
            {
              ...entryFormContent,
              contractType: "purchase",
              id: undefined,
            },
          ];
        }

        if (config.view === "transportOrders") {
          newEntries = [
            {
              ...entryFormContent,
              count: repeatCount,
              id: undefined,
            },
          ];
        }

        if (config.view === "forwardingOrders") {
          newEntries =
            typeof repeatCount === "number"
              ? Array.from({ length: repeatCount }, () => ({
                  ...entryFormContent,
                  id: undefined,
                }))
              : [];
        }

        const setDataResponse = await setDefaultData(newEntries);
        if (
          setDataResponse === null ||
          setDataResponse?.some((entry) => !!entry?.error)
        ) {
          const errorObj =
            setDataResponse?.find((entry) => !!entry?.error)?.error ?? null;
          setSubmitting(false);
          if (errorObj) {
            showToast({
              severity: "error",
              summary: "Błąd",
              detail: extractApiError(errorObj),
            });
            setSubmitting(false);
            return;
          }
        }
      }

      // After create
      if (!hasError) {
        showToast({
          severity: "success",
          summary: "Sukces",
          detail: "Dodano nowy wpis.",
        });
        dispatch(createFormSlice.actions.closeForm());
        setEntryFormContent({});
        setRepeatCount(1);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // const showDialog = () => {
  //   dispatch(createFormSlice.actions.openForm({ mode: "create" }));
  //   setEntryFormContent({});
  //   setRepeatCount(1);
  // };

  const onClose = () => {
    dispatch(createFormSlice.actions.closeForm());
    setEntryFormContent({});
    setRepeatCount(1);
  };

  // Group columns by formGroup
  const { grouped, noSectionColumns } = useMemo<{
    grouped: Record<string, PossibleColumnProps<RowFromView<TView>>[]>;
    noSectionColumns: PossibleColumnProps<RowFromView<TView>>[];
  }>(() => {
    const groupedColumns = columns.reduce(
      (acc, column) => {
        // Don't render fields with formGroup "none"
        if (column.formGroup === "none") {
          return acc;
        }
        if (column.formGroup && formGroupLabel[column.formGroup]) {
          if ("field" in column) {
            acc.grouped[column.formGroup] = [
              ...(acc.grouped[column.formGroup] || []),
              column as PossibleColumnProps<RowFromView<TView>>,
            ];
          }
        } else {
          if ("field" in column) {
            acc.noSectionColumns = [
              ...acc.noSectionColumns,
              column as PossibleColumnProps<RowFromView<TView>>,
            ];
          }
        }
        return acc;
      },
      {
        grouped: {},
        noSectionColumns: [],
      } as {
        grouped: Record<string, PossibleColumnProps<RowFromView<TView>>[]>;
        noSectionColumns: PossibleColumnProps<RowFromView<TView>>[];
      },
    );
    Object.keys(groupedColumns.grouped).forEach((group) => {
      if (
        groupedColumns.grouped[group].some((c) => Boolean(c.formGroupIndex))
      ) {
        groupedColumns.grouped[group] = groupedColumns.grouped[group].sort(
          (a, b) => (a?.formGroupIndex ?? 0) - (b?.formGroupIndex ?? 0),
        );
      }
    });
    return groupedColumns;
  }, [columns]);

  // Render fields based on grouped columns
  function renderField(column: PossibleColumnProps<RowFromView<TView>>) {
    const isVehicleField =
      column.field === "trucks" || column.field === "trailers";
    if (
      isVehicleField &&
      !entryFormContent.driver &&
      inputContext === "deliveryDialog"
    ) {
      return (
        <div key={column.field} className="field">
          <label>{column.header}</label>
          <small style={{ fontStyle: "italic", color: "#999" }}>
            Najpierw wybierz kierowcę
          </small>
        </div>
      );
    }
    if (
      (column.field === "product" && entryFormContent.salesContract) ||
      (column.field === "product" && entryFormContent.purchaseContract)
    ) {
      return (
        <div key={column.field} className="field">
          <label>{column.header}</label>
          <small style={{ fontStyle: "italic", color: "#999" }}>
            Zestaw produktów z umowy
          </small>
          <span>
            {selectedSalesContract
              ? selectedSalesContract?.data.product.name
              : (selectedPurchaseContract?.data.product.name ?? "")}
          </span>
        </div>
      );
    }
    return (
      <div key={column.field} className="field">
        <label>
          {column.header}
          <label>
            {(typeof column.required === "function"
              ? column.required(entryFormContent)
              : column.required) && <span style={{ color: "red" }}> *</span>}
          </label>
        </label>
        <CellEditor<RowFromView<TView>>
          column={column}
          value={entryFormContent[column.field as keyof RowFromView<TView>]}
          rowData={entryFormContent}
          dropdownConfigs={childDropdownConfigs}
          onChange={(value) => handleCellContentChange(column.field, value)}
          inputContext={inputContext}
        />
      </div>
    );
  }

  // Order form sections based on formGroupLabel
  const orderedSections = Object.keys(formGroupLabel)
    .filter((section) => grouped[section])
    .map(
      (section) =>
        [section, grouped[section]] as [string, (typeof grouped)[string]],
    );

  // required columns
  // const requiredColumns = columns.filter((column) => column.required);

  const repeatCountSelector = (
    <div className="field count">
      <small>
        Liczba wpisów do dodania
        <span style={{ color: "red" }}> *</span>
      </small>
      <div className="flex-container">
        <div className="manual-count flex-container">
          <div className="counter-radio flex-container">
            <RadioButton
              inputId="count_from_manual"
              name="repeatCount"
              value="count_from_manual"
              checked={typeof repeatCount === "number"}
              onChange={() => setRepeatCount(1)}
            />
            <label htmlFor="count_from_manual">Ustawić ręcznie (1-50)</label>
          </div>
          <InputNumber
            value={typeof repeatCount === "number" ? repeatCount : 1}
            showButtons
            min={1}
            max={50}
            onValueChange={(e) => setRepeatCount(e.value ?? 1)}
            placeholder="Ilość powtórzeń"
            disabled={typeof repeatCount !== "number"}
            className="manual-count-input"
          />
        </div>
        <div className="flex-container">
          <RadioButton
            inputId="count_from_sales_contract"
            name="repeatCount"
            value="from_sales_contract"
            checked={repeatCount === "from_sales_contract"}
            onChange={() => setRepeatCount("from_sales_contract")}
          />
          <label htmlFor="count_from_sales_contract">Z umowy sprzedaży</label>
        </div>
        <div className="flex-container">
          <RadioButton
            inputId="count_from_purchase_contract"
            name="repeatCount"
            value="from_purchase_contract"
            checked={repeatCount === "from_purchase_contract"}
            onChange={() => setRepeatCount("from_purchase_contract")}
          />
          <label htmlFor="count_from_purchase_contract">Z umowy kupna</label>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        header="Doday nowy"
        visible={isOpen}
        footer={
          <div className="dialog-footer">
            <Button
              label="Anuluj"
              className="p-button-text"
              onClick={() => onClose()}
            />
            <div className="flex-container">
              {repeatCount === "from_purchase_contract" &&
                !entryFormContent.purchaseContract && (
                  <small style={{ color: "red" }}>Wybierz umowę kupna</small>
                )}
              {repeatCount === "from_sales_contract" &&
                !entryFormContent.salesContract && (
                  <small style={{ color: "orangered" }}>
                    Wybierz umowę sprzedaży
                  </small>
                )}
              <Button
                label="Zapisz"
                icon="pi pi-check"
                onClick={() => {
                  void handleSave().catch(console.log);
                }}
                disabled={
                  !isEntryFormContentValid ||
                  isSubmitting ||
                  (repeatCount === "from_sales_contract" &&
                    !entryFormContent.salesContract) ||
                  (repeatCount === "from_purchase_contract" &&
                    !entryFormContent.purchaseContract)
                }
              />
            </div>
          </div>
        }
        onHide={() => onClose()}
        style={{ width: "25vw" }}
        breakpoints={{
          "10000px":
            config.view === "transportOrders" ||
            config.view === "forwardingOrders"
              ? "70vw"
              : "40vw",
          "2000px":
            config.view === "transportOrders" ||
            config.view === "forwardingOrders"
              ? "90vw"
              : "40vw",
          "960px": "75vw",
          "641px": "95vw",
        }}
      >
        <div className="flex flex-column">
          {config.allowMultipleNewEntries &&
            config.view === "transportOrders" &&
            repeatCountSelector}
          {config.allowMultipleNewEntries &&
            config.view === "forwardingOrders" && (
              <div className="field count">
                <label htmlFor="count_from_manual">
                  Ustawić ręcznie (1-50)
                  <span style={{ color: "red" }}> *</span>
                </label>
                <InputNumber
                  value={typeof repeatCount === "number" ? repeatCount : 1}
                  showButtons
                  min={1}
                  max={50}
                  onValueChange={(e) => setRepeatCount(e.value ?? 1)}
                  placeholder="Ilość powtórzeń"
                  disabled={typeof repeatCount !== "number"}
                  style={{ maxWidth: "32.5%" }}
                />
              </div>
            )}

          {/* {requiredColumns.length > 0 &&
            requiredColumns.map((column) => (
              <div key={column.field} className="field">
                <label>
                  {column.header}
                  {column.required && <span style={{ color: "red" }}> *</span>}
                </label>
                <CellEditor
                  column={column}
                  value={entryFormContent[column.field as keyof RowFromView<TView>] ?? ""}
                  rowData={entryFormContent}
                  dropdownConfigs={childDropdownConfigs}
                  onChange={(value) =>
                    handleCellContentChange(column.field, value)
                  }
                  inputContext={inputContext}
                />
              </div>
            ))} */}
          <div
            className={
              config.view === "transportOrders" ||
              config.view === "forwardingOrders"
                ? "form-groups"
                : undefined
            }
          >
            {orderedSections.map(([section, sectionColumns]) => (
              <div
                key={section}
                className={
                  config.view === "transportOrders" ||
                  config.view === "forwardingOrders"
                    ? "form-section"
                    : undefined
                }
              >
                <h3>{formGroupLabel[section] || section}</h3>
                {sectionColumns.map(renderField)}
              </div>
            ))}
            {noSectionColumns.length > 0 && (
              <div
                className={
                  config.view === "transportOrders" ||
                  config.view === "forwardingOrders"
                    ? "form-section"
                    : undefined
                }
              >
                {config.view === "transportOrders" ||
                  (config.view === "forwardingOrders" && <h3>Inne</h3>)}
                {noSectionColumns.map(renderField)}
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}
